import { type IAgentAssignmentItem } from '../../../interface/agent-assignment';
import { CopyValue } from '../../utils/copy';
import { UnreadPill } from '../../utils/unread-pill';
import { type CoverageState, deriveCoverage } from '../overview/quick-analytics/utils';
import { agentDisplayName, avatarTint, initials } from './utils';

const PILL_COPY: Record<CoverageState, { tone: string; label: string }> = {
	uploaded: { tone: 'success', label: 'Result uploaded' },
	pending: { tone: 'warning', label: 'Awaiting upload' },
	conflict: { tone: 'danger', label: 'Disputed' },
	agentUnavailable: { tone: 'warning', label: 'Account unavailable' },
	unassigned: { tone: 'neutral', label: 'Unassigned' },
};

interface IAgentSummaryStripProps {
	item: IAgentAssignmentItem;
	level: string;
	code: string;
	/** Unread messages from this agent, so the count survives a switch to the Result or Map tab. */
	unread?: number;
}

/**
 * Tab-independent header for the Agent Coverage drawer: entity code + level +
 * the assigned agent's name + a single overall status pill (from deriveCoverage).
 * Always visible above the tab-bar so the supervisor never loses context when
 * switching tabs.
 */
const AgentSummaryStrip = ({ item, level, code, unread = 0 }: IAgentSummaryStripProps) => {
	const coverage = deriveCoverage(item);
	const pill = PILL_COPY[coverage.state];
	const agent = item.agent;
	const name = agent ? agentDisplayName(agent) : 'No agent assigned';

	return (
		<div className='ca-summary'>
			<span
				className='ca-summary__avatar'
				style={agent ? { background: avatarTint(code) } : undefined}
				aria-hidden='true'
			>
				{agent ? initials(agent.firstName, agent.lastName, agent.userName) : '—'}
			</span>
			<div className='ca-summary__text'>
				<span className='ca-summary__eyebrow'>
					{level} · <span className='ca-summary__code'>{code}</span>
					<CopyValue
						value={code}
						label='code'
						size={14}
					/>
				</span>
				<span className='ca-summary__name'>
					{name}
					<UnreadPill count={unread} />
				</span>
			</div>
			<span className={`ca-summary__pill ca-summary__pill--${pill.tone}`}>{pill.label}</span>
		</div>
	);
};

export default AgentSummaryStrip;
